<template>
  <form
    ref="form"
    @submit.prevent="next"
  >
    <c-title>Antragsformular</c-title>

    <p class="mt-8 mb-8">
      Alle Felder außer den explizit ausgenommenen sind Pflichtfelder.
    </p>
    <c-form-fieldset
      legend="Antragsteller:in"
      class="mb-8"
    >
      <c-form-text
        v-model="fields.contact_person"
        :errors="errors.contact_person"
        class="mb-8"
        label="Ansprechpartner:in"
        autocomplete="name"
        required
      />
      <c-form-text
        v-model="fields.function"
        :errors="errors.function"
        class="mb-8"
        label="Funktion (Fächer, Fachkoordination etc.)"
        required
      />
      <c-form-text
        v-model="fields.school"
        :errors="errors.school"
        class="mb-8"
        label="Schule"
        autocomplete="school"
        required
      />
      <c-form-text
        v-model="fields.address"
        :errors="errors.address"
        class="mb-8"
        label="Straße und Hausnummer"
        autocomplete="street-address"
        maxlength="100"
        required
      />
      <c-form-columns :columns="[1, 5]">
        <c-form-text
          v-model="fields.postal_code"
          :errors="errors.postal_code"
          class="mb-8"
          label="Postleitzahl"
          autocomplete="postal-code"
          inputmode="numeric"
          pattern="[0-9]*"
          minlength="5"
          maxlength="5"
          required
        />
        <c-form-text
          v-model="fields.city"
          :errors="errors.city"
          class="mb-8"
          label="Ort"
          autocomplete="address-level2"
          maxlength="100"
          required
        />
      </c-form-columns>
      <c-form-text
        v-model="fields.contact_number"
        :errors="errors.contact_number"
        class="mb-8"
        label="Telefonnummer"
        autocomplete="tel"
        required
      />
      <c-form-text
        v-model="fields.email"
        :errors="errors.email"
        class="mb-8"
        label="E-Mail-Adresse"
        type="email"
        autocomplete="email"
        required
      />
    </c-form-fieldset>
    <c-form-fieldset
      legend="Angaben zum Projekt"
      class="mb-8"
    >
      <c-form-fieldset
        legend="Das Projekt ist folgendem Förderschwerpunkt zuzuorden (Mehrfachauswahl möglich)"
        class="mb-8 focal-support"
      >
        <input
          class="fieldset-validator visually-hidden"
          required
          :disabled="!published"
          :value="hasSelectedOption ? 'valid' : ''"
        >
        <c-form-columns :columns="[2,2,2]">
          <c-form-checkbox
            v-model="fields.democracy_education"
            :errors="errors.democracy_education"
            class="mb-3"
            label="Demokratiebildung"
          />
          <c-form-checkbox
            v-model="fields.personality_development"
            :errors="errors.personality_development"
            class="mb-3"
            label="Persönlichkeitsentwicklung"
          />
          <c-form-checkbox
            v-model="fields.vocational_training"
            :errors="errors.vocational_training"
            class="mb-3"
            label="berufliche Bildung & Übergänge"
          />
        </c-form-columns>
      </c-form-fieldset>
      <c-form-text
        v-model="fields.project_title"
        :errors="errors.project_title"
        class="mb-8"
        label="Projekttitel"
        required
      />
      <c-form-text
        v-model="fields.event_dates"
        :errors="errors.event_dates"
        class="mb-8"
        label="Zeitraum"
        required
      />
      <c-form-text
        v-model="fields.event_location"
        :errors="errors.event_location"
        class="mb-8"
        label="Ort(e)"
        required
      />
      <c-form-text
        v-model="fields.target_group"
        :errors="errors.target_group"
        class="mb-8"
        label="Zielgruppe"
        :help="`max. 300 Zeichen, inkl. Leerzeichen`"
        required
        tag="textarea"
        maxlength="300"
        rows="3"
      />
      <c-form-text
        v-model="fields.further_involved"
        :errors="errors.further_involved"
        class="mb-8"
        label="Weitere Projektbeteiligte"
      />
      <c-form-text
        v-model="fields.message_text"
        :errors="errors.message_text"
        class="mb-8"
        label="Kurzbeschreibung des Projekts"
        :help="`max. 1500 Zeichen, inkl. Leerzeichen`"
        required
        tag="textarea"
        maxlength="1500"
        rows="20"
      />
    </c-form-fieldset>
    <c-form-fieldset
      legend="Kosten/Finanzierung"
      class="mb-8"
    >
      <c-form-text
        v-model="fields.total_cost"
        :errors="errors.total_cost"
        class="mb-8"
        label="Gesamtkosten"
        required
      />
      <c-form-text
        v-model="fields.secured_funds"
        :errors="errors.secured_funds"
        class="mb-8"
        label="Anderweitig beantragte und/ oder bewilligte Mittel (kein Pflichtfeld)"
      />
      <c-form-text
        v-model="fields.application_amount"
        :errors="errors.application_amount"
        class="mb-8"
        label="Antragssumme Claussen-Simon-Stiftung"
        required
      />
      <c-form-text
        :errors="errors.detailed_project_description"
        class="mt-5 mb-8"
        type="file"
        :file="fields.detailed_project_description"
        label="Formlose Projektbeschreibung inkl. Durchführungskonzept und Zeitplan (2 bis 4 Seiten), die deutliche Antworten auf die W-Fragen enthalten sollte (wer macht was wann und wo mit wem und für wen?)"
        :help="`Erlaubte Dateiendungen: PDF, DOC, DOCX. Maximale Dateigröße: 5 MB.`"
        accept=".pdf,.doc,.docx"
        required
        @change="fields.detailed_project_description = $event"
      />
      <c-form-text
        :errors="errors.cost_plan_file"
        class="mt-5 mb-8"
        type="file"
        :file="fields.cost_plan_file"
        label="Kosten- & Finanzierungsplan"
        :help="`Erlaubte Dateiendungen: PDF, DOC, DOCX, XLS und XLSX. Maximale Dateigröße: 5 MB.`"
        accept=".pdf,.doc,.docx,.xls,.xlsx"
        required
        @change="fields.cost_plan_file = $event"
      />
      <c-form-text
        :errors="errors.support_by_principals"
        class="mt-5 mb-8"
        type="file"
        :file="fields.support_by_principals"
        label="Unterstützung durch die Schulleitung (hierfür können Sie die <a href='https://www.claussen-simon-stiftung.de/de/bildung-schule/claussen-simon-fonds-schulen/' target='_blank'>Vorlage auf unserer Website</a> nutzen)"
        :help="`Erlaubte Dateiendungen: PDF, DOC, DOCX, XLS und XLSX. Maximale Dateigröße: 5 MB.`"
        accept=".pdf,.doc,.docx,.xls,.xlsx"
        required
        @change="fields.support_by_principals = $event"
      />
    </c-form-fieldset>
    <c-button>
      Nächster Schritt
    </c-button>
  </form>
</template>

<script>
import BRequestStep from './BRequestStep.vue'

export default {
  name: 'VRequestStep0',

  extends: BRequestStep,

  data () {
    return {
      fields: {
        contact_person: '',
        function: '',
        school: '',
        address: '',
        postal_code: '',
        city: '',
        contact_number: '',
        email: '',
        democracy_education: false,
        personality_development: false,
        vocational_training: false,
        project_title: '',
        event_dates: '',
        event_location: '',
        target_group: '',
        further_involved: '',
        message_text: '',
        application_amount: '',
        secured_funds: '',
        detailed_project_description: '',
        cost_plan_file: '',
        support_by_principals: ''
      }
    }
  },

  computed: {
    step: () => 0,
    id: () => 'reportData',
    hasSelectedOption () {
      const keysToCheck = ['democracy_education', 'personality_development', 'vocational_training']
      return keysToCheck.some(key => this.fields[key])
    },
    published () {
      return this.$store.state.publishedInfo.published
    }
  },

  watch: {
    fields: {
      handler (newFields) {
        const fieldsetValidator = document.getElementsByClassName('fieldset-validator')[0]
        const keysToCheck = ['democracy_education', 'personality_development', 'vocational_training']
        if (!keysToCheck.some(key => this.fields[key]) && fieldsetValidator) {
          fieldsetValidator.setCustomValidity('Wählen Sie bitte mindestens eine Option aus.')
        } else {
          fieldsetValidator.setCustomValidity('')
        }
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }
</style>
